import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useErrorToast } from '@/components/layers/ReactQueryProvider';
import { Api } from '@/core/api';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { getAverageGsi, getPreviousWeekAverageGsi } from '../components/utils/incidentGsi';

const QUERY_KEY = 'asset-gsi';
const STALE_TIME = 5 * 60 * 1000;

type UseAssetGsiDataParams = {
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  enabled?: boolean;
};

export const useAssetGsiData = ({
  location,
  latitude,
  longitude,
  enabled = true,
}: UseAssetGsiDataParams) => {
  const { orgId } = useCurrentUser();
  const normalizedLocation = location?.trim() ?? '';

  const { data: gsiData = [], isLoading, ...query } = useQuery({
    queryKey: [QUERY_KEY, normalizedLocation, latitude, longitude],
    queryFn: () =>
      Api.incidents.getAssetGsi({
        location: normalizedLocation || undefined,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
      }),
    enabled: !!orgId && enabled,
    staleTime: STALE_TIME,
  });

  useErrorToast(query);

  const averageGsi = useMemo(() => getAverageGsi(gsiData), [gsiData]);
  const previousAverageGsi = useMemo(() => getPreviousWeekAverageGsi(gsiData), [gsiData]);

  return {
    gsiData,
    averageGsi,
    previousAverageGsi,
    isLoading,
    ...query,
  };
};
